import React, {
  useState,
} from 'react';
import { useQuery } from '@apollo/react-hooks';
import { TextFieldProps } from '@material-ui/core/TextField';
import { Autocomplete } from '@material-ui/lab';
import { CircularProgress, TextField } from '@material-ui/core';
import SmartLock from '../../types/SmartLock';
import useDebounce from '../../hooks/useDebounce';
import { SEARCH_SMART_LOCKS } from '../../graphql/queries/SmartLockDetails';

type Props = {
  onSmartLockChange: (smartLock: SmartLock) => void,
  initialValue?: string;
  styles?: object;
  addLabel?: boolean;
} & TextFieldProps;

const SmartLockSearch: React.FC<Props> = ({
  onSmartLockChange, initialValue, styles, addLabel, ...textFieldProps
}) => {
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce(search, 100);
  const handleSearch = (event : React.KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLInputElement;
    setSearch(target.value);
  };

  const { data, loading } = useQuery<{ smartLocks: SmartLock[] }>(
    SEARCH_SMART_LOCKS,
    {
      variables: {
        sn: debouncedSearch,
      },
      fetchPolicy: 'network-only',
    },
  );

  const handleChange = (event: any, chosenSelect: { label: string, value: SmartLock } | null) => {
    const initialProp = {
      serial_number: '',
    };
    onSmartLockChange(chosenSelect?.value ?? initialProp);
  };

  const availableSmartLocks = (data?.smartLocks.map(item => ({
    value: item,
    label: `${item.serial_number}`,
  }))) || [];

  if (initialValue && !availableSmartLocks.find(smartLock => smartLock.value.serial_number === initialValue)) {
    availableSmartLocks.push({
      value: {
        serial_number: initialValue,
      },
      label: initialValue,
    });
  }

  return (
    <>
      <div style={{ marginTop: '15px' }}>
        <Autocomplete
          options={availableSmartLocks}
          getOptionLabel={option => option.label}
          noOptionsText="No smart locks were found"
          onChange={handleChange}
          loading={loading}
          defaultValue={initialValue
            ? { label: initialValue, value: { serial_number: initialValue } } : undefined}
          renderInput={params => (
            <TextField
              {...params}
              label={addLabel && 'Smart Lock Serial Number'}
              fullWidth
              onKeyUp={handleSearch}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
              {...textFieldProps}
            />
          )}
        />
      </div>
    </>
  );
};

export default SmartLockSearch;
